/**
 * Feed health (zeecore GET /ingestion/manage/feeds): for each ingestion feed,
 * whether it is being called and whether its calls work — the two different
 * ways data stops arriving. A source's watermark cannot tell them apart, nor
 * see one failing feed behind a working sibling.
 */
import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle, Clock, MinusCircle, RefreshCw } from 'lucide-react';
import { API_ENDPOINTS } from '../../config/api';
import { authenticatedFetch } from '../../utils/authenticatedFetch';

export interface FeedHealth {
  feed: string;
  source: string;
  label: string;
  status: 'ok' | 'failing' | 'overdue' | 'not_expected';
  last_attempt_at: string;
  last_success_at?: string;
  consecutive_failures: number;
  last_error?: string;
  last_count?: number;
  usual_gap_ms?: number;
  overdue_by_ms?: number;
  /** Why no schedule runs this feed, when status is not_expected. */
  not_expected_reason?: string;
  refused?: {
    last_refused_at?: string;
    consecutive_refusals: number;
    refused_by?: { job_id: string; sources: string[]; started_at?: string; status: string };
  };
}

const when = (iso?: string) =>
  iso ? new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'never';

/** "15 min", "16 h", "2 d". */
export function span(ms: number): string {
  const min = Math.round(ms / 60_000);
  if (min < 90) return `${min} min`;
  const h = Math.round(min / 60);
  return h < 48 ? `${h} h` : `${Math.round(h / 24)} d`;
}

/** What is wrong with a feed, in a sentence; null when nothing is (a feed no schedule runs is not a problem). */
export function feedProblem(f: FeedHealth): string | null {
  if (f.status === 'not_expected') return null;
  const refused = f.refused?.refused_by
    ? ` Its last ${f.refused.consecutive_refusals === 1 ? 'run was' : `${f.refused.consecutive_refusals} runs were`} refused: another import (${f.refused.refused_by.sources.join(', ')}${f.refused.refused_by.started_at ? `, started ${when(f.refused.refused_by.started_at)}` : ''}) was still ${f.refused.refused_by.status}.`
    : '';
  if (f.status === 'failing') {
    return `Calls failing (${f.consecutive_failures} in a row): ${f.last_error ?? 'unknown error'}. Last success ${when(f.last_success_at)}.${refused}`;
  }
  if (f.status === 'overdue') {
    return `Not called since ${when(f.last_attempt_at)} — it usually runs every ${span(f.usual_gap_ms ?? 0)}.${refused || ' No run was asked for, or it was not reached.'}`;
  }
  return refused ? refused.trim() : null;
}

/** Why a not-expected feed is idle, with how its last call went. */
export function feedNotExpectedNote(f: FeedHealth): string | null {
  if (f.status !== 'not_expected') return null;
  const last = f.consecutive_failures > 0
    ? `Its last call failed: ${f.last_error ?? 'unknown error'}.`
    : `Last success ${when(f.last_success_at)}.`;
  return `${f.not_expected_reason ?? 'Not on a schedule.'} ${last}`;
}

export function FeedHealthPanel() {
  const [feeds, setFeeds] = useState<FeedHealth[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await authenticatedFetch(API_ENDPOINTS.ingestionManage.feeds());
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as { feeds?: FeedHealth[] };
      setFeeds(data.feeds ?? []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load feed health');
    }
  }, []);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 60_000);
    return () => clearInterval(t);
  }, [load]);

  const problems = (feeds ?? []).filter((f) => feedProblem(f) !== null);
  const expected = (feeds ?? []).filter((f) => f.status !== 'not_expected');
  const idle = (feeds?.length ?? 0) - expected.length;
  const idleNote = idle === 0 ? '' : ` ${idle} not on a schedule.`;

  return (
    <section aria-label="Feed health" className="bg-card rounded-lg border border-border p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Feed health</h3>
        <button type="button" onClick={() => void load()} className="text-muted-foreground hover:text-foreground" aria-label="Refresh feed health">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      <p className="text-xs text-muted-foreground">
        Each scheduled feed: whether its calls work, and whether it is being called as often as usual. Recorded from routine imports; a feed shows here after its first one.
      </p>
      {error && <p role="alert" className="text-xs text-destructive">{error}</p>}
      {feeds && feeds.length === 0 && <p className="text-sm text-muted-foreground">No routine imports recorded yet.</p>}
      {feeds && feeds.length > 0 && (
        <>
          <p className={`text-sm ${problems.length ? 'text-warning' : 'text-success'}`}>
            {problems.length === 0
              ? (expected.length === 1 ? 'The scheduled feed is running as usual.' : `All ${expected.length} scheduled feeds are running as usual.`)
              : `${problems.length} of ${expected.length} scheduled feeds need attention.`}
            {idleNote}
          </p>
          <ul className="divide-y divide-border">
            {feeds.map((f) => {
              const problem = feedProblem(f);
              const note = feedNotExpectedNote(f);
              const Icon = f.status === 'failing' ? AlertTriangle
                : f.status === 'overdue' ? Clock
                  : f.status === 'not_expected' ? MinusCircle
                    : CheckCircle;
              const tone = f.status === 'failing' ? 'text-destructive'
                : f.status === 'overdue' || problem ? 'text-warning'
                  : f.status === 'not_expected' ? 'text-muted-foreground'
                    : 'text-success';
              return (
                <li key={f.feed} className="py-2 flex items-start gap-2">
                  <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${tone}`} />
                  <div className="min-w-0">
                    <p className="text-sm text-foreground">
                      {f.label}
                      <span className="ml-2 text-xs text-muted-foreground">
                        last called {when(f.last_attempt_at)}
                        {f.usual_gap_ms ? ` · usually every ${span(f.usual_gap_ms)}` : ''}
                        {f.status === 'ok' && f.last_count !== undefined ? ` · ${f.last_count} items` : ''}
                      </span>
                    </p>
                    {problem && <p className={`text-xs ${tone} break-words`}>{problem}</p>}
                    {note && <p className="text-xs text-muted-foreground break-words">{note}</p>}
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
}
